/**
 * Box rows: which questions draw Top 2 / Bottom 2 / NPS rows, where those rows sit, and how a
 * researcher's choices are stored (M4S-5607). Spec: docs/pdlc/crosstabs-top-2-box.md, Part 2.
 *
 * `scale.ts` decides what a question IS (a worded scale, a number scale, NPS, a list) and which
 * answers its Top 2 and Bottom 2 net. This module decides what happens next:
 *
 * - **The choices.** A crosstab's per-question choices are a `BoxChoiceMap` (qid → the six-field
 *   `PerQuestionSummary`), saved on the record. `parseBoxChoices` reads whatever a record or a
 *   browser slot holds and rebuilds it in ONE canonical form, because the compute dirty check
 *   compares JSON and JSONB hands object keys back ordered by length. `withBoxChoice` writes one
 *   question's choice and stores only what differs from the default, so switching a choice on and
 *   back off leaves the map byte-identical and Run unlit. `pruneBoxChoices` is what a crosstab-wide
 *   switch does to the exceptions (M4S-5618's rule: changing the default clears them).
 * - **The position.** `placeBoxRows` puts each net immediately above the first of its answers that
 *   is shown, as Juhi's workbook does. It is generic over any net that carries member codes, so
 *   Top 2 / Bottom 2, M4S-5599's Promoters / Neutral / Detractors and M4S-5606's
 *   `netPosition: 'above-members'` variable nets all use it.
 *
 * - **The gate.** `resolveBoxRows` asks `scale.ts`'s `boxSwitches`, the one gate, which rows a
 *   question draws, and adds each side's members and where the researcher moved it. Compute, the
 *   rail's summary and M4S-5592's summary tables all read it, so the rail says exactly what the
 *   table draws.
 *
 * The module is pure and persists nothing, so it runs on the client and on the server.
 */

import { overrideFor, type AnswerEditsMap } from '@/lib/crosstab/answer-edits';
import { resolveMatrixView, toMatrixGrid, type MatrixEditsMap } from '@/lib/crosstab/matrix-edits';
import { resolveByColumn } from '@/lib/crosstab/matrix-orientation';
import type {
    CrosstabQuestionDto,
    CrosstabQuestionOptionDto,
} from '@/lib/crosstab/question-formatter';
import {
    PER_QUESTION_SUMMARY_KEYS,
    boxEntryOf,
    boxSwitches,
    isRetiredBoxOptionId,
    scaleOf,
    type BoxSwitchOpts,
    type NpsBands,
    type PerQuestionSummary,
    type ScaleInfo,
    type ScaleKind,
} from '@/lib/crosstab/scale';

// ── The stored form ───────────────────────────────────────────────────────────

/** The anchor that places a net after the question's own rows. */
export const BOX_END = '$end';

/** The longest anchor a stored choice may hold. Anchors are answer keys, which are short. */
export const BOX_ANCHOR_MAX = 500;

/**
 * A crosstab's per-question choices, keyed by UPPER-CASED qid. Each entry holds only what differs
 * from the default, in the field order of `PER_QUESTION_SUMMARY_KEYS`.
 */
export type BoxChoiceMap = Record<string, PerQuestionSummary>;

/** The crosstab-wide switch a choice is pruned against. */
export type BoxSide = 'top' | 'bottom' | 'nps' | 'top1';

const ANCHOR_KEYS: ReadonlySet<string> = new Set(['topAbove', 'bottomAbove']);

const SIDE_KEY = {
    top: 'showTopBox',
    bottom: 'showBottomBox',
    nps: 'showNPS',
    top1: 'showTop1Box',
} as const;

/** Code-unit order, never localeCompare, whose result depends on the ICU build. */
function byCodeUnits(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * One entry in the canonical form: booleans stay booleans, anchors stay strings of 1 to
 * `BOX_ANCHOR_MAX` characters, anything else is dropped, and the fields come in
 * `PER_QUESTION_SUMMARY_KEYS` order. Null when nothing is left.
 */
function canonicalEntry(raw: unknown): PerQuestionSummary | null {
    if (!isPlainObject(raw)) return null;
    const entry: Record<string, boolean | string> = {};
    for (const key of PER_QUESTION_SUMMARY_KEYS) {
        const value = raw[key];
        if (ANCHOR_KEYS.has(key)) {
            if (typeof value === 'string' && value.length >= 1 && value.length <= BOX_ANCHOR_MAX) {
                entry[key] = value;
            }
        } else if (typeof value === 'boolean') {
            entry[key] = value;
        }
    }
    return Object.keys(entry).length ? (entry as PerQuestionSummary) : null;
}

/**
 * Reads whatever a record, a browser slot or a request body holds, and never throws. Qids are
 * upper-cased and sorted by code units, each entry is rebuilt in the canonical field order, junk
 * is dropped, and an entry with nothing left is dropped. So `parseBoxChoices(parseBoxChoices(x))`
 * is `parseBoxChoices(x)`, and a map that came back from JSONB in another key order parses to the
 * JSON of its own save.
 *
 * Two spellings of one qid (`OOHLIKE` and `oohlike`) merge field by field, in code-unit order of
 * the keys as given. Upper-case sorts first, so the lower-case spelling wins a field both hold.
 * Nothing writes two spellings; the order only makes the result the same on every run.
 */
export function parseBoxChoices(raw: unknown): BoxChoiceMap {
    if (!isPlainObject(raw)) return {};
    const merged = new Map<string, Record<string, unknown>>();
    for (const key of Object.keys(raw).sort(byCodeUnits)) {
        if (!key.trim()) continue;
        const entry = canonicalEntry(raw[key]);
        if (!entry) continue;
        const qid = key.toUpperCase();
        merged.set(qid, { ...merged.get(qid), ...entry });
    }
    const out: BoxChoiceMap = {};
    for (const qid of [...merged.keys()].sort(byCodeUnits)) {
        const entry = canonicalEntry(merged.get(qid));
        if (entry) out[qid] = entry;
    }
    return out;
}

/** One question's change. A `null` anchor means the default position ("above its answers"). */
export interface BoxChoicePatch {
    showTopBox?: boolean;
    showTop1Box?: boolean;
    showBottomBox?: boolean;
    showNPS?: boolean;
    reversed?: boolean;
    topAbove?: string | null;
    bottomAbove?: string | null;
}

/** What the default is, so that only a difference is stored. */
export interface BoxChoiceDefaults {
    /** Studio picks this question, so the crosstab-wide Top 2 / Bottom 2 switch reaches it. */
    picked: boolean;
    /** The crosstab-wide switches. */
    showTopBox?: boolean;
    showTop1Box?: boolean;
    showBottomBox?: boolean;
    showNPS?: boolean;
}

const FLAG_KEYS = ['showTopBox', 'showTop1Box', 'showBottomBox', 'showNPS', 'reversed'] as const;
const ANCHOR_PATCH_KEYS = ['topAbove', 'bottomAbove'] as const;

/**
 * The map with one question's choice changed, in canonical form.
 *
 * - **Only differences are stored.** A question's Top 2 default is the crosstab-wide switch AND
 *   Studio's pick; its NPS default is the crosstab-wide NPS switch; `reversed` defaults to off. A
 *   value equal to its default is removed, so on-then-off leaves the map as it was.
 * - **A `null` anchor is the default position** and is removed.
 * - **Changing `reversed` clears both anchors** unless the same patch sets them: an anchor picked
 *   for the old Top 2 would name the wrong answer once the sides swap (spec Part 1 §4.7).
 */
export function withBoxChoice(
    map: unknown,
    qid: string,
    patch: BoxChoicePatch,
    defaults: BoxChoiceDefaults,
): BoxChoiceMap {
    const key = qid.toUpperCase();
    const current = parseBoxChoices(map);
    const entry: Record<string, unknown> = { ...current[key] };

    const defaultOf: Record<(typeof FLAG_KEYS)[number], boolean> = {
        showTopBox: !!defaults.showTopBox && defaults.picked,
        showTop1Box: !!defaults.showTop1Box && defaults.picked,
        showBottomBox: !!defaults.showBottomBox && defaults.picked,
        showNPS: !!defaults.showNPS,
        reversed: false,
    };
    for (const flag of FLAG_KEYS) {
        const want = patch[flag];
        if (want === undefined) continue;
        if (want === defaultOf[flag]) delete entry[flag];
        else entry[flag] = want;
    }

    if (patch.reversed !== undefined && patch.reversed !== (current[key]?.reversed === true)) {
        for (const anchor of ANCHOR_PATCH_KEYS) {
            if (patch[anchor] === undefined) delete entry[anchor];
        }
    }
    for (const anchor of ANCHOR_PATCH_KEYS) {
        const want = patch[anchor];
        if (want === undefined) continue;
        if (want === null || want === '') delete entry[anchor];
        else entry[anchor] = want;
    }

    return parseBoxChoices({ ...current, [key]: entry });
}

/**
 * The map after a crosstab-wide switch changed: that side's on/off is removed from every entry, so
 * the switch means what it says ("Top 2 Box off" is none anywhere). Positions and `reversed` are
 * kept, and no catalog is needed. With the switch off, one question can still be switched on,
 * because on differs from that question's default.
 */
export function pruneBoxChoices(map: unknown, side: BoxSide): BoxChoiceMap {
    const key = SIDE_KEY[side];
    const out: Record<string, unknown> = {};
    for (const [qid, entry] of Object.entries(parseBoxChoices(map))) {
        const rest: Record<string, unknown> = { ...entry };
        delete rest[key];
        out[qid] = rest;
    }
    return parseBoxChoices(out);
}

// ── Placement ─────────────────────────────────────────────────────────────────

/**
 * The anchor key of a flat answer: its option codes, upper-cased, de-duplicated, sorted and joined
 * with "+". A matrix answer's key is its choice key or its NET key (M4S-5618), used as it is.
 */
export function rowKey(codes: readonly string[]): string {
    return [...new Set(codes.map((code) => code.toUpperCase()))].sort(byCodeUnits).join('+');
}

/** One of a question's own rows, in the order the table lists them. */
export interface BoxPlaceItem<T> {
    /** What an anchor names: `rowKey(option_id)` on a flat question, a choice or NET key on a matrix. */
    key: string;
    /** The survey codes this item holds. A net names its members by these. */
    codes: readonly string[];
    /** The row drawn for it, or null when nothing is drawn (hidden, or an empty cell). */
    row: T | null;
}

/** A net to place: the row, the codes it adds up, and where the researcher moved it, if they did. */
export interface BoxPlaceNet<T> {
    row: T;
    members: readonly string[];
    /** An answer key, or `BOX_END`. Ignored unless it names a shown item. */
    above?: string;
}

/**
 * The question's own rows with each net placed, in order. The rest of the block (Create Variable
 * nets, Mean, Median, NPS Score) is the caller's, after these.
 *
 * Each net goes **immediately above the first of its members that is shown**. When every member is
 * hidden, or has no cell, it goes above the first shown row after the last of them; and when there
 * is none, after the question's own rows. An anchor wins only when it names a shown item, and
 * `BOX_END` puts the net after the question's own rows; an anchor that names nothing shown falls
 * back to the default, so a stale one is harmless. Nets that land in the same place keep the order
 * they were passed in. Codes are compared case-insensitively.
 */
export function placeBoxRows<T>(
    items: readonly BoxPlaceItem<T>[],
    nets: readonly BoxPlaceNet<T>[],
): T[] {
    const holds = (item: BoxPlaceItem<T>, members: ReadonlySet<string>) =>
        item.codes.some((code) => members.has(code.toUpperCase()));
    const shown = (item: BoxPlaceItem<T>) => item.row !== null;

    /** The index of the item each net goes above; `items.length` is after the last one. */
    const before = nets.map((net): number => {
        if (net.above === BOX_END) return items.length;
        if (net.above !== undefined) {
            const anchored = items.findIndex((item) => shown(item) && item.key === net.above);
            if (anchored >= 0) return anchored;
        }
        const members = new Set(net.members.map((code) => code.toUpperCase()));
        const firstShown = items.findIndex((item) => shown(item) && holds(item, members));
        if (firstShown >= 0) return firstShown;
        const last = items.reduce((at, item, i) => (holds(item, members) ? i : at), -1);
        if (last < 0) return items.length;
        const after = items.findIndex((item, i) => i > last && shown(item));
        return after >= 0 ? after : items.length;
    });

    const out: T[] = [];
    for (let i = 0; i <= items.length; i++) {
        nets.forEach((net, n) => {
            if (before[n] === i) out.push(net.row);
        });
        const row = items[i]?.row;
        if (row !== undefined && row !== null) out.push(row);
    }
    return out;
}

// ── Which rows a question draws ───────────────────────────────────────────────

/** The crosstab-wide switches and the per-question map that decide which box rows are drawn. */
export interface BoxRowOpts extends BoxSwitchOpts {
    perQuestionT2B2?: Readonly<Record<string, PerQuestionSummary>>;
}

/** One side that is on: the codes it adds up, and where the researcher moved it, if they did. */
export interface ResolvedBoxSide {
    members: string[];
    /** An answer key or `BOX_END`, as stored. `placeBoxRows` ignores one that names nothing shown. */
    above?: string;
}

export interface ResolvedBoxRows {
    /** The question's scale, flipped when the researcher reversed it. */
    scale: ScaleInfo;
    entry: PerQuestionSummary | undefined;
    top?: ResolvedBoxSide;
    /** M4S-5777 (X79): the single Top Box, placed above its answer like Top 2. */
    top1?: ResolvedBoxSide;
    bottom?: ResolvedBoxSide;
    /** The NPS bands, when the NPS switch reaches this question. */
    nps?: NpsBands;
}

/**
 * The box rows a question draws. On or off is `boxSwitches`: the question's own choice wins, else
 * the crosstab-wide switch reaches it if Studio picks it (`firstPass`). Members come from
 * `scale.ts` on the question as surveyed, so hiding, moving or renaming an answer never changes
 * them. `qid` is the row id as the crosstab holds it; the lookup in the map is by upper-cased qid.
 */
export function resolveBoxRows(
    q: CrosstabQuestionDto,
    opts: BoxRowOpts,
    qid: string = q.qid,
): ResolvedBoxRows {
    const entry = boxEntryOf(opts.perQuestionT2B2, qid);
    const scale = scaleOf(q, { flip: entry?.reversed === true });
    const sw = boxSwitches(scale, entry, opts);
    const side = (members: string[] | null, above: string | undefined): ResolvedBoxSide | null =>
        members ? { members, ...(above ? { above } : {}) } : null;
    const top = sw.showTop ? side(scale.top, entry?.topAbove) : null;
    const top1 = sw.showTop1 ? side(scale.topBox, undefined) : null;
    const bottom = sw.showBottom ? side(scale.bottom, entry?.bottomAbove) : null;
    return {
        scale,
        entry,
        ...(top ? { top } : {}),
        ...(top1 ? { top1 } : {}),
        ...(bottom ? { bottom } : {}),
        ...(sw.showNps && scale.nps ? { nps: scale.nps } : {}),
    };
}

/**
 * Whether `scale.ts` can box the question: it has a Top 2 and a Bottom 2. NPS and a question of
 * fewer than four answers do not.
 */
export function isBoxable(q: CrosstabQuestionDto): boolean {
    const scale = scaleOf(q);
    return scale.top !== null && scale.bottom !== null;
}

/** Whether Studio picks the question, so the crosstab-wide switch reaches it. There is no second rule. */
export function isFirstPassBox(q: CrosstabQuestionDto): boolean {
    const scale = scaleOf(q);
    return scale.top !== null && scale.bottom !== null && scale.firstPass;
}

/** One of a question's own options as compute draws it. */
export interface ListedOption {
    option: CrosstabQuestionOptionDto;
    /** Hidden by a saved row override, so no row is drawn for it. */
    hidden: boolean;
}

/**
 * A flat question's own options as its table lists them: the saved override when there is one, else
 * the question's options, without the Top 2 / Bottom 2 options the mapper used to draw
 * (`isRetiredBoxOptionId`). Hidden options stay in the list, marked, because where a net sits
 * depends on them. Compute and the rail's summary both read this list, so they cannot disagree.
 */
export function listedOptions(
    q: CrosstabQuestionDto,
    override?: readonly CrosstabQuestionOptionDto[],
): ListedOption[] {
    return (override ?? q.options)
        .filter((option) => !option.option_id.some((id) => isRetiredBoxOptionId(q, id)))
        .map((option) => ({ option, hidden: override !== undefined && option.hidden === true }));
}

/**
 * The codes each of a flat question's listed options holds when a box row is placed, in list order.
 * An option holds its own codes, except a net the researcher built (`isNet`, from Merge in the
 * options dialog): merging keeps the members that carry one code, so the net repeats codes an
 * answer in the list still holds, and it holds only those no other listed answer holds. Merging
 * removes a member that is itself a combined option (a code per wave), and the net then is the only
 * row for that member's codes and holds them. So a net dragged to the top of the list is a place a
 * row can be moved to (`rowKey` stays its key), but it does not pull Bottom 2 above Top 2.
 * Hidden answers count as holders, as they do in `placeBoxRows`.
 */
export function heldCodes(listed: readonly ListedOption[]): string[][] {
    const heldByAnswers = new Set(
        listed.flatMap(({ option }) =>
            option.isNet ? [] : option.option_id.map((id) => id.toUpperCase()),
        ),
    );
    return listed.map(({ option }) =>
        option.isNet
            ? option.option_id.filter((id) => !heldByAnswers.has(id.toUpperCase()))
            : [...option.option_id],
    );
}

// ── What the rail is given ────────────────────────────────────────────────────

/** Everything `buildBoxRowSummary` reads from the crosstab's settings. */
export interface BoxRowSettings extends BoxRowOpts {
    matrixEdits?: MatrixEditsMap;
    /** M4S-5777 (K3): the record's answer edits on flat questions, read before `rowOverrides`. */
    answerEdits?: AnswerEditsMap;
    perQuestionMatrixTranspose?: Record<string, boolean>;
    matrixByColumn?: boolean;
}

/** One side of a question's Top 2 / Bottom 2, as the rail shows it. */
export interface BoxSideSummary {
    /** The answers it adds up, as the survey words them, best (or worst) first. */
    labels: string[];
    on: boolean;
    /**
     * Where the researcher put it, as the table draws it: a shown answer's key, or `BOX_END`.
     * Undefined for "above its answers", and for a stored anchor the table would ignore (it names a
     * hidden or missing answer, or anchors do not apply), so the rail never shows a position the
     * table does not draw.
     */
    above?: string;
}

/** One question in the rail's "Top 2 / Bottom 2 by question". */
export interface BoxRowEntry {
    qid: string;
    text: string;
    /** What `scale.ts` calls it. Never 'none': a question that is not a scale is not listed. */
    kind: Exclude<ScaleKind, 'none'>;
    /**
     * A matrix question, drawn as one table per statement. Its Top 2 / Bottom 2 switch also adds
     * the question's summary table (M4S-5592), which the rail says.
     */
    matrix: boolean;
    /** Studio picks it: the crosstab-wide switch reaches it. Every NPS question is reached. */
    picked: boolean;
    /**
     * A matrix drawn By Column, which has no box rows. `top.on` and `bottom.on` are the switches
     * (what `boxSwitches` says) and stay true for such a matrix that is switched on, though compute
     * draws nothing, so the rail reads this first and shows both sides as unavailable (C4).
     */
    byColumn: boolean;
    /** A Sits choice means something: false for a matrix By Column and for one without axes. */
    anchorsApply: boolean;
    /** The researcher's Reverse scale is on. */
    reversed: boolean;
    top: BoxSideSummary;
    bottom: BoxSideSummary;
    /** M4S-5777 (X79): the single Top Box: its answer and whether it is drawn. */
    top1: BoxSideSummary;
    /** The shown answers with their anchor keys, in the order the table lists them. */
    answers: { key: string; label: string }[];
    /**
     * NPS questions only: whether the nets are drawn. Their Top 2 and Bottom 2 sides are empty and
     * off. A 0-10 matrix is listed too, but compute draws no NPS nets on matrices yet, so its switch
     * is `unavailable` and never `on`: the rail says why instead of offering a switch that does
     * nothing.
     */
    npsSwitch?: { on: boolean; unavailable?: boolean };
}

/**
 * The rail's one memo: an entry for each active row question that can have Top 2 / Bottom 2 (a
 * worded scale, a number scale, or a list), or NPS. Derived from the catalog, the live settings and
 * the saved row overrides, and it writes nothing, so opening a crosstab still makes no POST.
 *
 * The answers and the resolved position follow what compute draws: a flat question's listed
 * options without the hidden ones, or a matrix's shown answer choices and nets in the view's order.
 */
export function buildBoxRowSummary(
    questions: readonly CrosstabQuestionDto[],
    activeRows: readonly string[],
    settings: BoxRowSettings,
    rowOverrides?: Readonly<Record<string, CrosstabQuestionOptionDto[]>>,
): BoxRowEntry[] {
    const byQid = new Map(questions.map((q) => [q.qid.toUpperCase(), q]));
    const seen = new Set<string>();
    const entries: BoxRowEntry[] = [];
    for (const rowQid of activeRows) {
        const upper = rowQid.toUpperCase();
        const q = byQid.get(upper);
        if (!q || seen.has(upper)) continue;
        seen.add(upper);

        const box = resolveBoxRows(q, settings, rowQid);
        const { scale } = box;
        if (scale.kind === 'none') continue;
        const isNps = scale.kind === 'nps';
        if (!isNps && scale.top === null) continue;

        const grid = toMatrixGrid(q);
        const byColumn =
            !!grid &&
            resolveByColumn(rowQid, settings.perQuestionMatrixTranspose, settings.matrixByColumn);
        const anchorsApply = grid ? grid.source === 'axes' && !byColumn : true;
        const answers = grid
            ? resolveMatrixView(grid, settings.matrixEdits?.[upper])
                  .choices.filter((choice) => !choice.hidden)
                  .map((choice) => ({ key: choice.key, label: choice.label }))
            : listedOptions(q, overrideFor(q, settings.answerEdits, rowOverrides))
                  .filter((listed) => !listed.hidden)
                  .map(({ option }) => ({ key: rowKey(option.option_id), label: option.option }));

        const drawnAt = (above: string | undefined) =>
            anchorsApply &&
            above !== undefined &&
            (above === BOX_END || answers.some((answer) => answer.key === above))
                ? above
                : undefined;
        const side = (
            sideAnswers: { label: string }[],
            on: boolean,
            above: string | undefined,
        ): BoxSideSummary => {
            const at = drawnAt(above);
            return { labels: sideAnswers.map((a) => a.label), on, ...(at ? { above: at } : {}) };
        };

        entries.push({
            qid: q.qid,
            text: q.question,
            kind: scale.kind,
            matrix: !!grid,
            picked: isNps || scale.firstPass,
            byColumn,
            anchorsApply,
            reversed: box.entry?.reversed === true,
            top: side(scale.topAnswers, !!box.top, box.entry?.topAbove),
            bottom: side(scale.bottomAnswers, !!box.bottom, box.entry?.bottomAbove),
            top1: side(scale.topBoxAnswers, !!box.top1, undefined),
            answers,
            // M4S-5777 (X80): NPS is drawn on a matrix By Row (one set per statement); By Column
            // has no box rows, so there the switch says why it is unavailable.
            ...(isNps
                ? {
                      npsSwitch:
                          grid && byColumn ? { on: false, unavailable: true } : { on: !!box.nps },
                  }
                : {}),
        });
    }
    return entries;
}
